import React from 'react';
import { Menu, MenuItem } from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  DriveFileRenameOutline as RenameIcon,
  Timeline as TimelineIcon,
  InfoOutlined as InfoIcon,
  FolderShared as FolderSharedIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { FileContextMenuProps } from '../types';
import { isDownloadableEntry } from '../childrenModel';
import { shareMenuLabelKey } from '../shareTargets';
import { downloadMenuLabelKey } from '../bulkDownload';
import { isPlottableFile } from '../utils';
import { canLinkToFlight } from '../../flight-links/flightLinkModel';
import { useTranslation } from '../../../i18n';

const FileContextMenu: React.FC<FileContextMenuProps> = ({
  anchorEl,
  selectedFile,
  onClose,
  onDownload,
  onRename,
  onDelete,
  onPlot,
  onDetails,
  onShareObject,
  onLinkFlight,
  shareTargetCount = 1,
  downloadTargetCount = 1,
}) => {
  const { t } = useTranslation();

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
      {/* `isDownloadableEntry`, not `!isFolder`: an R-9 entry whose `s3_key`
          spells no path has no address either, and R-9's rule is to hide the
          affordance rather than offer one the API must refuse.

          With several files selected the item fetches all of them and says how
          many (#1692), whichever entry of the selection was right-clicked. */}
      {selectedFile && (downloadTargetCount > 1 || isDownloadableEntry(selectedFile)) && (
        <MenuItem onClick={onDownload} data-testid="file-context-download">
          <DownloadIcon sx={{ mr: 1 }} />
          {t(downloadMenuLabelKey(downloadTargetCount), { count: downloadTargetCount })}
        </MenuItem>
      )}
      {selectedFile && !selectedFile.isFolder && !!selectedFile.flight_id && onDetails && (
        <MenuItem onClick={onDetails}>
          <InfoIcon sx={{ mr: 1 }} />
          {t('fileManagement.details')}
        </MenuItem>
      )}
      {/* THE Share action — the only one this menu offers (STD-029 R-12, B-6).
          It grants an object share: the file's bytes, or a whole folder
          including files added to it later.

          There used to be a second item beside it that shared the *flight* a
          file resolves to, because bytes and flight record were separate
          conveyances and collapsing them would have made record-sharing
          inexpressible. R-7 removed that reason for READING: since #1334 an
          object share carries its files' flight records and analysis verdicts,
          so for read the one grant says everything the two used to. It does not
          say the write half — flight share `write` conveys editing the flight
          record, which no object share grants (§3 matrix in
          `docs/STD-029-file-model.md`) — which is exactly why the flight-share
          ENDPOINTS are untouched: the bulk drone-serial dialog on the Shared
          view still grants them, at either permission, for files scattered
          across folders, and the existing grants keep working. What retired is
          this menu's route to them.

          Gated on `object_id` rather than on `isFolder`, because what makes an
          entry shareable is having a `fleet.file_objects` row. An object still
          served by the legacy S3 arm has none, and there is nothing for a share
          to point at — so the item is hidden rather than offered and refused.
          That case disappears once the legacy arm is retired.

          `onShareObject` is undefined for a non-admin caller, which is what
          hides it from users the API would refuse. */}
      {selectedFile && !!selectedFile.object_id && onShareObject && (
        <MenuItem onClick={onShareObject} data-testid="file-context-share-object">
          <FolderSharedIcon sx={{ mr: 1 }} />
          {t(shareMenuLabelKey(shareTargetCount, !!selectedFile.isFolder), {
            count: shareTargetCount,
          })}
        </MenuItem>
      )}
      {/* Link a .bin log to its flight record (#1694), so the flight's shares
          convey it. Any signed-in user who can see the log (Jakob,
          2026-11-02); the server refuses what may not be linked and the dialog
          says why. The fallback to the prompt that follows a .bin upload. */}
      {selectedFile && onLinkFlight && canLinkToFlight(selectedFile) && (
        <MenuItem onClick={onLinkFlight} data-testid="file-context-link-flight">
          <LinkIcon sx={{ mr: 1 }} />
          {t('flightLinks.menuItem')}
        </MenuItem>
      )}
      {selectedFile &&
        isDownloadableEntry(selectedFile) &&
        isPlottableFile(selectedFile.name) &&
        onPlot && (
          <MenuItem onClick={onPlot}>
            <TimelineIcon sx={{ mr: 1 }} />
            {t('fileManagement.plot')}
          </MenuItem>
        )}
      {/* Rename and Delete are gated on `object_id` as of STD-029 B-3, and the
          gate is the same rule the share item above uses. Both writes are now
          id-addressed (R-11) — `PATCH` / `DELETE /api/file-objects/{id}` — so an
          entry served by the flights arm with no registry row has nothing for
          the request to name. R-9 says such an entry degrades by *hiding*
          id-keyed affordances rather than by erroring, so the items are withheld
          instead of offered and refused.

          Delete was previously unconditional — the one menu item every entry
          got — because the path-addressed handler had a legacy S3 arm that could
          delete an object with no row. That arm was deleted with the endpoint;
          the honest consequence is that a rowless legacy object is no longer
          deletable here, and pretending otherwise would be a button that 404s.
          The population shrinks and never grows (#1278's C-gate), and B-4 ends
          it by giving every listed entry a row. */}
      {selectedFile && selectedFile.isFolder && !!selectedFile.object_id && (
        <MenuItem onClick={onRename} data-testid="file-context-rename">
          <RenameIcon sx={{ mr: 1 }} />
          {t('fileManagement.rename')}
        </MenuItem>
      )}
      {selectedFile && !!selectedFile.object_id && (
        <MenuItem onClick={onDelete} sx={{ color: 'error.main' }} data-testid="file-context-delete">
          <DeleteIcon sx={{ mr: 1 }} />
          {t('fileManagement.delete')}
        </MenuItem>
      )}
    </Menu>
  );
};

export default FileContextMenu;
